import { TAPIPayload, shortcuts, safeShortcuts, runTask } from '@myua/api';
import { WebkitFile } from '@myua/dropzone';
import { pickBy } from 'lodash';
import { Creative, CreativeGet, Graphic, GraphicGet, ManagerSave } from '../creativeAnalyzer/model';
import { NodeMin, OauthURL } from '../creativeAnalyzer/modules/publisher/model';
import {
  AdAccount,
  FacebookAdOptions,
  ManagingAccount,
} from '../creativeAnalyzer/modules/publisher/workflows/facebook/model';
import {
  GoogleAdGroup,
  GoogleAdsAccount,
  GoogleCampaign,
  GoogleLoginWebhook,
  GoogleTextTemplate,
  GoogleUploadCreativesRequest,
} from '../creativeAnalyzer/modules/publisher/workflows/googleads/model';
import {
  SuccessResponse,
  TikTokAccount,
  TikTokBusinessAccount,
  TikTokVideo,
} from '../creativeAnalyzer/modules/publisher/workflows/tiktok/model';
import {
  CreatePackPayload,
  MiniPayload,
  UnityApp,
} from '../creativeAnalyzer/modules/publisher/workflows/unity/model';
import { IFavorite } from '../creativeHub/modules/favorite/model';
import { ConversionState } from '../creativeHub/modules/fileManager/model';
import { TrashINode } from '../creativeHub/modules/trashPage/types';
import {
  FontLicenseModerationItem,
  ModerationFile,
  toModerationFile,
  UpdateFontLicensePayload,
} from '../contentModeration/modules/fonts/types';
import { getBlob } from '../creativeHub/modules/uploader/uploadSingleFile.saga';
import { APIScenarioFactory, APIScenarioReturn } from './apiSagaFactory';
import {
  CaFbProject,
  CreativeType,
  INode,
  CurrentUser,
  LicenseStatus,
  Project,
  ProjectLocalizationBack,
  Tag,
  UserRole,
  MisleadLevel,
  ConceptType,
} from './model';
import { Interrupt } from './scenarioFactory';
import { BLOptions, CreativePatchOptions, CreativeFolderServer } from './modelCreativeFolder';
import {
  PlacementStatusModalApplovin,
  PlacementStatusModalFacebook,
  PlacementStatusModalGoogle,
  PlacementStatusModalMintegral,
  PlacementStatusModalTiktok,
  PlacementStatusModalUnity,
  PlacementStatusModalYoutube,
} from '../creativeAnalyzer/modules/navigatorTable/model';
import { SearchINode } from '../creativeHub/modules/search/model';
import { CHNode } from './node';
import { Share } from '../creativeHub/model';
import { Dictionary } from '../creativeHub/modules/creativeWorkflow/legacy_models/model';
import { sendfile } from './utils/xhr';
import { nonUndefined } from './utils/nonNull';
import { z } from 'zod';
import { Settings } from '../creativeHub/modules/app/model';
import { User } from '@myua/api/dist/types';
import type { CreateFontLicensePayload, LicenseType } from './fontLicense/model';

const { get, post, patch, put } = shortcuts;

export type PlacementData = {
  creative_id: number;
  social_state: {
    youtube: PlacementStatusModalYoutube[];
    unity: PlacementStatusModalUnity[];
    google: PlacementStatusModalGoogle[];
    facebook: PlacementStatusModalFacebook[];
    tiktok: PlacementStatusModalTiktok[];
    applovin: PlacementStatusModalApplovin[];
    mintegral: PlacementStatusModalMintegral[];
  };
};
export type CopyMoveProps = {
  node_id: number;
  new_parent_node_id: number;
  overwrite: boolean;
  new_name?: string;
};

/**
 * удочерённые функции из ../creativeHub/modules/XXX/api
 * они находятся в отдельном объекте, а не в основном api,
 * потому что используются другими частями кода без обертки, которую дает api
 *
 * по возможности надо переселять эти функции в api и
 * оборачивать в APIScenarioFactory
 * @deprecated
 */
export const adopted = {
  logs: {
    send: (actions: any) => post({ url: `/api/crhub/logs/statement`, payload: { actions } }),
  },
  sharingAPI: {
    createShare: (
      node_ids: number[],
      name: string,
      expired_at: string,
    ): Promise<
      TAPIPayload<{
        sharing: {
          id: string;
          name: string;
          created_at: string; //datetime
          expired_at: string; //datetime
        };
      }>
    > => {
      const url = _urls.crhub.fs.sharing;
      return post({
        url,
        payload: {
          node_ids,
          name,
          expired_at,
        },
      });
    },
  },
  sharedLinksAPI: {
    loadShares: ({
      ordering,
      search,
      page,
    }: {
      ordering?: string;
      search?: string;
      page?: number;
    }) => {
      return safeShortcuts.get<{ sharing_list: Share[]; count: number }>({
        url: _urls.crhub.fs.sharing,
        query: { ordering, search, page },
      });
    },
    saveExpiration: (shareId: string, expiredAt: string) => {
      const url = _urls.crhub.fs.sharing;
      const payload = { sharing_id: shareId, expired_at: expiredAt };
      return put({ url, payload });
    },
    deleteSharedLinks: (sharing_ids: string[]) => {
      return shortcuts.delete({ url: _urls.crhub.fs.sharing, payload: { sharing_ids } });
    },
  },

  removerAPI: {
    deleteNode: (node_id: number) => {
      return post({ url: `/api/crhub/fs/delete`, query: { node_id } });
    },

    deleteCreativeFile: (node_id: number) => {
      return shortcuts.delete({ url: _urls.crhub.bl.creatives.files(node_id) });
    },
  },
  testingButtonAPI: {
    submitForTesting: (creative_id: number) => {
      const url = `/api/crhub/bl/creatives/${creative_id}/to-testing`;
      return patch({ url });
    },
  },
};

// adopted functions end

const delay = (ms: number) =>
  new Promise((ok) => {
    setTimeout(ok, ms);
  });
// fs
/**
 * @deprecated use type inference from api endpoints
 */
export type FSLoadNodes = APIScenarioReturn<{ nodes: INode[] }>;
/**
 * @deprecated use type inference from api endpoints
 */
export type FSRename = APIScenarioReturn<{ node: INode }>;

export type loadProjectsLocalizationsReturn = {
  results: ProjectLocalizationBack[];
  count: number;
  next: string | null;
  previous: string | null;
};

export type loadCAProjectsReturn = CaFbProject[];

export type loadTopDashboardCreativesReturn = { table: Creative[]; total_row: number };

export type loadTopDashboardGraphicsReturn = Graphic[];

export type ApplovinCampaign = {
  id: number;
  name: string;
  campaign_id: string;
  platform: string;
  category: string;
  tracking_method: string;
  status: boolean;
  created_at: string;
  app: number;
};
export type ApplovinApp = { id: number; bundle_id: string; app_id: string };

export const CPPSchema = z.object({
  cpp_id: z.string(),
  cpp_name: z.string(),
  cpp_type: z.string(),
});

export type CPP = z.infer<typeof CPPSchema>;

export const ApplovinCreativePayloadSetSchema = z.object({
  campaigns: z.array(z.number()),
  end_cards: z.array(z.number()),
  name: z.string(),
  creative: z.number(),
  countries: z.array(z.string()),
  languages: z.array(z.string()),
  formats: z.array(z.string()),
  cpp: z.array(CPPSchema),
  applovin_creative_set_id: z.string().optional(),
});

export type ApplovinCreativePayloadSet = z.infer<typeof ApplovinCreativePayloadSetSchema>;

export type ApplovinAutoCreativePayloadSet = {
  campaigns: number[];
  end_cards: number[];
  // name: string;
  creatives: {
    creative: number;
    languages: string[];
  }[];
  countries: string[];
  languages: string[];
  cpp: CPP[];
  applovin_creative_set_id?: string;
};

export const ApplovinCombinedCreativePayloadSetSchema = z.object({
  campaigns: z.array(z.number()),
  creatives: z.array(
    z.object({
      creative: z.number(),
      languages: z.array(z.string()),
    }),
  ),
  end_cards: z.array(z.number()),
  formats: z.array(z.string()),
  cpp: z.array(CPPSchema),
  languages: z.array(z.string()),
  countries: z.array(z.string()),
  upload_default: z.boolean(),
  name: z.string(),
  applovin_creative_set_id: z.string().optional(),
});

export type ApplovinCombinedCreativePayloadSet = z.infer<
  typeof ApplovinCombinedCreativePayloadSetSchema
>;

export type CoreTaskResult<T> =
  | {
      status: 'PENDING';
      result: null;
    }
  | {
      status: 'SUCCESS';
      result: T;
    }
  | {
      status: 'FAILURE';
      result: {
        message: string;
      };
    };
export type FigmaImportTaskResult = CoreTaskResult<{
  node_id: number;
}>;

/**
 * Ответ single-shot запроса статуса Celery-задачи `/api/core/task/<id>/`.
 * В отличие от {@link CoreTaskResult} не сужает `status` до SUCCESS/FAILURE —
 * Celery может вернуть промежуточные STARTED/RETRY, которые обрабатываются
 * вызывающей стороной (маппинг статусов), а не считаются ошибкой.
 */
export type CoreTaskStatusResponse = {
  id?: string;
  status?: string;
  result?: { message?: string } | null;
};

export type Genre = { name: string; id: number };
export type Intro = { name: string; id: number };
export const _urls = {
  crhub: {
    fs: {
      metadata: (nodeID: number, shareID?: string | null) => {
        const url = `/api/crhub/fs/metadata/${nodeID}`;
        if (!shareID) return url;
        return `${url}?share_id=${shareID}`;
      },
      list_parents: '/api/crhub/fs/list_parents',
      sharing: `/api/crhub/fs/sharing/`,
      favourites: `/api/crhub/fs/favourites/`,
      permissions: {
        chub: (user_id: number | string) => `/api/crhub/fs/permissions/${user_id}`,
        alab: (user_id: number | string) => `/api/crhub/fs/permissions/asset-lab/${user_id}`,
      },
      node: (nodeId: number) => `/api/crhub/fs/node/${nodeId}`,
    },
    bl: {
      creatives: {
        id: (id?: string) => `/api/crhub/bl/creatives${id ? `/${id}` : ''}`,
        types: (typeId?: string) => `/api/crhub/bl/creatives/types${typeId ? `/${typeId}` : ''}`,
        files: (id?: number) => {
          const suffix = id ? `/${id}` : '';
          return `/api/crhub/bl/creatives/files${suffix}`;
        },
      },
      projects: {
        export: (projectID: string) => `/api/crhub/bl/projects/${projectID}/export}`,
      },
    },
  },
};

export const api = {
  core: {
    async task<T>(task_id: string, signal?: AbortSignal) {
      // eslint-disable-next-line no-constant-condition
      while (true) {
        signal?.throwIfAborted();
        const result = await get<CoreTaskResult<T>>({ url: `/api/core/task/${task_id}/` });
        if (result.response.status === 'SUCCESS') {
          return result.response.result;
        } else if (result.response.status === 'PENDING') {
          await delay(1000);
          continue;
        } else {
          // я не знаю какие еще есть статусы, скорее всего фейл, поэтому кидаем ошибку
          return Promise.reject(result.response.result.message);
        }
      }
    },
    /**
     * Single-shot статус Celery-задачи (без внутреннего цикла опроса).
     * Опрос/маппинг статусов выполняется вызывающей стороной (см. applovin/statusMapping).
     * Namely: НЕ реджектит на не-SUCCESS/PENDING статусах, в отличие от {@link api.core.task}.
     */
    taskStatus: APIScenarioFactory((task_id: string) =>
      get<CoreTaskStatusResponse>({ url: `/api/core/task/${task_id}/` }),
    ),
  },
  figma: {
    request: APIScenarioFactory((nodeID: number) => {
      return get<{ task_id: string }>({
        url: `/api/crhub/figma/export?node_id=${nodeID}`,
      });
    }),
  },
  crhub: {
    settings: {
      load: () => safeShortcuts.get<Settings>({ url: `/api/crhub/settings/` }),
    },
    playables: {
      convert: APIScenarioFactory(
        (job: {
          source: number;
          social_network: string;
          filename: string;
          google_store_url: string;
          apple_store_url: string;
          project: number;
        }) => {
          const url = `/api/crhub/playables/convert/`;
          return post<any>({ url, payload: job });
        },
      ),
    },
    videoMerge: {
      merge: (payload: {
        parent_node: CHNode['id'];
        video_nodes: CHNode['id'][];
        packshot_node: CHNode['id'];
        name: string;
        transition_node?: CHNode['id'] | null;
        packshot_screen_folder?: CHNode['id'] | null;
        length?: number | null;
      }) =>
        safeShortcuts.post<{ task_id: string }>({
          url: `/api/crhub/video-editor/merge`,
          payload,
        }),
    },
    googleDriveImport: {
      uploadFromLink: (payload: { link: string; parent_node_id: CHNode['id'] }) =>
        safeShortcuts.post<{ task_id: string; direct_uploading_id: number }>({
          url: `/api/crhub/fs/upload_from_link`,
          payload,
        }),
    },
    library: {
      list_folder: APIScenarioFactory((nodeId: number) => {
        const url = `/api/crhub/library/list_folder`;
        const query: any = {};
        query.node_id = nodeId.toString();
        return get<{ nodes: INode[] }>({ url, query });
      }),
    },
  },
  mg: {
    users: {
      updateUserGroups: APIScenarioFactory(
        (userId: CurrentUser['id'], attach: UserRole[], detach: UserRole[]) => {
          const url = `/api/crhub/mg/users/${userId}/update-groups`;
          const payload = {
            attach,
            detach,
          };
          return post({ url, payload });
        },
      ),
      patch: APIScenarioFactory(
        (
          userId: CurrentUser['id'],
          payload: Partial<Omit<CurrentUser, 'team'> & { team: CurrentUser['team'] | number }>,
        ) => {
          const url = `/api/crhub/mg/users/${userId}`;
          return patch({ url, payload });
        },
      ),
      deactivate: APIScenarioFactory((userId: CurrentUser['id']) => {
        return post({
          url: `/api/crhub/mg/users/${userId}/deactivate`,
        });
      }),
    },
  },
  top: {
    dashboard: {
      loadManagers: APIScenarioFactory(
        (crhub_project_id: number, report_date_from: string, report_date_to: string) =>
          get({
            url: `/api/crhub/top/dashboard/managers`,
            query: {
              crhub_project_id: crhub_project_id.toString(10),
              report_date_from,
              report_date_to,
            },
          }),
      ),

      loadTopDashboardCreatives: APIScenarioFactory((payload: CreativeGet) =>
        post({
          url: `/api/crhub/top/dashboard/creatives`,
          payload,
        }),
      ),
      saveManager: APIScenarioFactory((payload: ManagerSave, projectTEMPORARY: number) =>
        post({
          url: `/api/crhub/top/dashboard/save_manager`,
          // TEMPORARY мы не должны сохранять проект тут!
          payload: { ...payload, project: projectTEMPORARY },
        }),
      ),
      loadTopDashboardGraphics: APIScenarioFactory((payload: GraphicGet) =>
        post({
          url: `/api/crhub/top/dashboard/graphics`,
          payload,
        }),
      ),
    },
  },
  oauth: {
    user: APIScenarioFactory(<T = User>() => {
      return get<T>({ url: '/api/core/oauth/user/' });
    }),
  },

  laws: {
    // License-type dictionary for font uploads (MYUA-16307), sorted by name.
    getLicenseTypes: () =>
      safeShortcuts.get<LicenseType[]>({ url: `/api/crhub/laws/license_types` }),
    // Server-side Jira task URL validation (format only — one legal task may cover
    // several fonts, so the URL is not unique). 200 { valid: true } or 400 { code, data }.
    validateFontLicense: (jira_task_url: string) =>
      safeShortcuts.post<{ valid: boolean }>({
        url: `/api/crhub/laws/validate_font_license`,
        payload: { jira_task_url },
      }),
    // Creates a FontLicense record BEFORE the upload, with no node attached
    // (POST /laws/font_licenses). The returned id is passed to the upload endpoints
    // as `font_license_ids`; the backend attaches it to the node it creates.
    // Available to any authenticated user (no moderation rights required).
    createFontLicense: (payload: CreateFontLicensePayload) =>
      safeShortcuts.post<{ id: number }>({
        url: `/api/crhub/laws/font_licenses`,
        payload,
      }),
    // --- Font license moderation (MYUA-16456) ---
    // Список файлов на модерацию. Бэкенд отдаёт пагинированный ответ с вложенным
    // узлом файла (имя), владельцем (создатель) и модератором; мапим results в строки таблицы.
    // status не задан → возвращаются лицензии во всех статусах (фильтр «All»).
    loadFontLicensesForModeration: async (
      status?: LicenseStatus,
    ): Promise<APIScenarioReturn<ModerationFile[]>> => {
      const res = await safeShortcuts.get<{
        count: number;
        next: string | null;
        previous: string | null;
        results: FontLicenseModerationItem[];
      }>({
        url: `/api/crhub/laws/font_licenses`,
        query: status ? { status } : {},
      });
      if (!res.success) {
        return { success: false, args: [], reason: Interrupt.API };
      }
      return {
        success: true,
        data: {
          response: res.data.response.results.map(toModerationFile),
        } as TAPIPayload<ModerationFile[]>,
        args: [],
      };
    },
    // Решение юриста по лицензии: PATCH /laws/font_licenses/{id}. Возвращает обновлённую лицензию.
    updateFontLicense: async (
      id: ModerationFile['id'],
      payload: UpdateFontLicensePayload,
    ): Promise<APIScenarioReturn<ModerationFile>> => {
      const res = await safeShortcuts.patch<FontLicenseModerationItem>({
        url: `/api/crhub/laws/font_licenses/${id}`,
        payload,
      });
      if (!res.success) {
        // Тело ошибки ({code, data}) нужно саге, чтобы показать причину отказа.
        return { success: false, args: [id, payload], reason: Interrupt.API, errorData: res.data };
      }
      return {
        success: true,
        data: { response: toModerationFile(res.data.response) } as TAPIPayload<ModerationFile>,
        args: [id, payload],
      };
    },
  },

  fav: {
    load: APIScenarioFactory(() => {
      return get<IFavorite[]>({ url: _urls.crhub.fs.favourites });
    }),
    add: APIScenarioFactory((node_id: number, name: string) => {
      const url = _urls.crhub.fs.favourites;
      const payload = {
        node_id,
        name,
      };
      return post<IFavorite>({ url, payload });
    }),
    delete: APIScenarioFactory((favoriteId: number) => {
      const url = _urls.crhub.fs.favourites;
      const payload = {
        id: favoriteId,
      };
      return shortcuts.delete({ url, payload });
    }),
    rename: APIScenarioFactory((favoriteId: number, name: string) => {
      return patch({
        url: _urls.crhub.fs.favourites,
        payload: {
          id: favoriteId,
          name,
        },
      });
    }),
  },
  trash: {
    load: APIScenarioFactory(() => {
      return get<{ nodes: TrashINode[] }>({ url: '/api/crhub/fs/trash/list' });
    }),
    parents: APIScenarioFactory((node_id: number) => {
      return get<{ nodes: INode[] }>({
        url: '/api/crhub/fs/trash/list_parents',
        query: { node_id },
      });
    }),
    recovery: APIScenarioFactory((node_id: number, overwrite: boolean) => {
      return post<INode>({
        url: '/api/crhub/fs/trash/recovery',
        payload: { node_id, overwrite },
      });
    }),
  },
  fs: {
    listParents: APIScenarioFactory((nodeId?: number, signal?: AbortSignal) => {
      const url = _urls.crhub.fs.list_parents;
      const query: any = {};
      if (nodeId !== undefined) {
        query.node_id = nodeId;
      }
      return get<{ nodes: INode[] }>({ url, query, signal });
    }),
    listShareParents: APIScenarioFactory((shareId: string, nodeId?: number) => {
      const url = `/api/crhub/fs/sharing/${shareId}/list_parents`;
      const query: any = {};
      if (nodeId !== undefined) {
        query.node_id = nodeId;
      }
      return get<{ nodes: INode[] }>({ url, query });
    }),
    updateFlags: APIScenarioFactory((nodeId: number, flags: string[]) => {
      const url = _urls.crhub.fs.node(nodeId);
      const payload = {
        flags,
      };
      return patch<{ node: INode }>({ url, payload });
    }),
    listAllDescendants: APIScenarioFactory((nodeID: number) =>
      get<{
        nodes: INode[];
      }>({
        url: `/api/crhub/fs/files/${nodeID}`,
      }),
    ),
    /**
     * Список файлов в папке
     */
    loadNodes: APIScenarioFactory((nodeID: number) => {
      const url = `/api/crhub/fs/list_folder`;
      const query: any = {};
      query.node_id = nodeID.toString();
      return get<{ nodes: INode[] }>({ url, query });
    }),
    loadShareNodes: APIScenarioFactory((shareID: string, nodeID?: number) => {
      const url = `/api/crhub/fs/sharing/${shareID}/list_folder`;
      const query: any = {};
      if (nodeID) {
        query.node_id = nodeID;
      }
      return get<{ nodes: INode[] }>({ url, query });
    }),
    rename: APIScenarioFactory((new_name: string, node_id: number) => {
      const url = `/api/crhub/fs/rename`;
      const payload = { new_name, node_id };
      return shortcuts.post<{ node: INode }>({ url, payload });
    }),
    copyNodeAsync: APIScenarioFactory(async (props: CopyMoveProps) => {
      const { new_parent_node_id, node_id, overwrite, new_name } = props;
      const task_id_response = await post<{ task_id: string }>({
        url: `/api/crhub/fs/node/copy_async`,
        payload: { node_id, new_parent_node_id, overwrite, new_name },
      });
      if (task_id_response.status !== 200) throw task_id_response;
      const { task_id } = task_id_response.response;

      const taskResult = await runTask<number>(task_id, 1000);
      if (!taskResult.success) {
        throw new Error(taskResult.error);
      }
      return {
        request: task_id_response.request,
        response: { id: taskResult.data },
        status: 200,
      };
    }),
    moveNode: APIScenarioFactory((props: CopyMoveProps) => {
      const { new_parent_node_id, node_id, overwrite } = props;
      return post<{ node: INode }>({
        url: `/api/crhub/fs/move`,
        payload: { node_id, new_parent_node_id, overwrite },
      });
    }),
    uploadFile: async (
      file: WebkitFile,
      parent_node_id: number,
      abortSignal?: AbortSignal,
      progressCallback?: (loaded: number, total: number) => void,
      overwrite?: boolean,
      // Ids of the FontLicense records created up-front via POST /laws/font_licenses.
      // Only present when the file carries fonts (a font itself, or an archive with
      // fonts inside); harmless otherwise. Sent as a comma-separated query param.
      fontLicenseIds?: number[],
    ) => {
      const { md5 } = await getBlob(file);

      const query: Record<string, string | number | undefined> = {
        parent_node_id: parent_node_id.toString(),
        filename: file.name,
        md5_of_file: md5,
        overwrite: overwrite ? 'true' : 'false',
      };
      if (fontLicenseIds?.length) {
        query.font_license_ids = fontLicenseIds.join(',');
      }

      return sendfile(
        {
          method: 'POST',
          payload: file,
          url: `/api/crhub/fs/upload_file`,
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          signal: abortSignal,
          query,
        },
        undefined,
        progressCallback,
      );
    },
    getUploadID: APIScenarioFactory(
      (
        file: WebkitFile,
        parent_node_id: number,
        part_size: number,
        md5_of_file: string,
        overwrite: boolean,
        // Ids of the FontLicense records created up-front via POST /laws/font_licenses.
        // Only present when the file carries fonts (a font itself, or an archive with
        // fonts inside); harmless otherwise.
        fontLicenseIds?: number[],
      ) => {
        // The multipart-init endpoint no longer returns `storage_id`; the file
        // node is created by the backend only after the last part succeeds and
        // the whole-file MD5 is verified (see uploadFilePart 201 response).
        return post<{
          id: number;
        }>({
          url: `/api/crhub/fs/create_multipart_upload`,
          payload: {
            filesize: file.size,
            filename: file.name,
            part_size,
            parent_node_id,
            md5_of_file,
            overwrite,
            // Licenses travel as a list of ids created beforehand; the backend keeps
            // them on the MultipartUpload and attaches them to the node once the
            // last part completes.
            ...(fontLicenseIds?.length ? { font_license_ids: fontLicenseIds } : {}),
          },
        });
      },
    ),
    uploadFilePart: async (
      currentPart: number,
      totalParts: number,
      blobData: { md5: string; arrayBuffer: ArrayBuffer },
      upload_id: number,
      abortSignal?: AbortSignal,
      progressCallback?: (loaded: number, total: number) => void,
    ): Promise<{ response: INode | null; status: number; success: boolean }> => {
      // NB: `sendfile` treats any 2xx as success and does NOT distinguish
      // statuses. The caller MUST inspect `status` explicitly:
      //  - intermediate part -> 202 (empty body, response === null)
      //  - last part         -> 201 with the node as a FLAT object in the response
      //    root (NodeSerializer(node).data + `perms`), NOT wrapped in { node }.
      // `md5_of_file` is intentionally NOT sent here (only in create_multipart_upload).
      return sendfile(
        {
          method: 'POST',
          payload: blobData.arrayBuffer,
          url: `/api/crhub/fs/upload_part`,
          headers: {
            'Content-Type': 'application/octet-stream',
          },
          signal: abortSignal,
          query: {
            md5_of_part: blobData.md5,
            part: currentPart + '/' + totalParts,
            upload_id,
          },
        },
        undefined,
        progressCallback,
      );
    },

    createFolder: APIScenarioFactory((name: string, parentNodeId: number) => {
      const payload: any = {
        name,
        flags: [],
      };
      if (parentNodeId) {
        payload.parent_node_id = parentNodeId;
      }
      return post<{ node: INode }>({ url: `/api/crhub/fs/create_folder`, payload });
    }),
    permissions: {
      post: APIScenarioFactory(
        (user_id: number, node_id: number, perms: string[], group: 'chub' | 'alab') => {
          return post<{ node: INode }>({
            url: _urls.crhub.fs.permissions[group](user_id),
            payload: { node_id, perms },
          });
        },
      ),
      del: APIScenarioFactory((user_id: number, id: number, group: 'chub' | 'alab') => {
        return shortcuts.delete({
          url: _urls.crhub.fs.permissions[group](user_id),
          payload: { id },
        });
      }),
      load: APIScenarioFactory((user_id: string, group: 'chub' | 'alab') => {
        return get({ url: _urls.crhub.fs.permissions[group](user_id) });
      }),
    },
    validateFilenames: APIScenarioFactory((filenames: string[]) =>
      post<{ validation_result: { filename: string; error: string | null }[] }>({
        url: '/api/crhub/fs/validate-filenames',
        payload: { filenames },
      }),
    ),
    createGoogleBulkLink: (node_ids: CHNode['id'][], share_id?: Share['id'] | null) =>
      safeShortcuts.post<{ task_id: string }>({
        url: share_id
          ? `/api/crhub/fs/sharing/${share_id}/download_bulk_direct`
          : `/api/crhub/fs/download_bulk_direct`,
        payload: { node_ids },
      }),
  },
  tags: {
    load: APIScenarioFactory(() => {
      return get<{ id: number; name: string }[]>({ url: `/api/crhub/fs/tags` });
    }),
    create: APIScenarioFactory((name: string, signal?: AbortSignal) => {
      return post<Tag>({ url: `/api/crhub/fs/tags`, payload: { name }, signal });
    }),
    apply: APIScenarioFactory((nodeId: number, tagIds: number[], signal?: AbortSignal) => {
      return patch<{ tags: number[] }>({
        url: _urls.crhub.fs.node(nodeId),
        payload: { tags: tagIds },
        signal,
      });
    }),
  },
  search: {
    searchByTag: APIScenarioFactory((tagIds: any, scope: number | null) => {
      const query: any = { ids: tagIds };
      if (scope !== null) {
        query.scope = scope;
      }
      return get<{ nodes: SearchINode[] }>({
        url: '/api/crhub/fs/list_folder/search-by-tag',
        query,
      });
    }),
    searchInTrash: APIScenarioFactory((search_query: string) => {
      const query = {
        search_query,
      };
      return get<{ nodes: TrashINode[] }>({ url: '/api/crhub/fs/trash/list', query });
    }),
    // `ai=true` — семантический поиск по разметке Plot Recognition (MYUA-16771, бэкенд MYUA-16493).
    // В текстовом режиме `search_type` не отправляем вовсе: на бэкенде дефолт `text`, так контракт
    // запроса не меняется.
    search: APIScenarioFactory<any, any>((search_query: string, scope: number, ai?: boolean) => {
      const query: Record<string, number | string> = {
        search_query,
      };
      if (scope) query.node_id = scope;
      if (ai) query.search_type = 'semantic';
      return get<{ results: { nodes: SearchINode[]; count: number } }>({
        url: '/api/crhub/fs/list_folder/search',
        query,
      });
    }),
  },
  bl: {
    loadLocalizationForProject: APIScenarioFactory(
      (
        projectID: CaFbProject['crhub_project_id'],
        page: number,
        page_size: number,
        order_by: string | null,
        query?: Record<string, string>,
      ) => {
        const _query: Record<string, string> = {
          page: page.toString(10),
          page_size: page_size.toString(10),
          ...(query ?? {}),
        };
        if (order_by !== null) _query['order_by'] = order_by;
        return get<loadProjectsLocalizationsReturn>({
          url: `/api/crhub/bl/projects/${projectID}/localization`,
          query: _query,
        });
      },
    ),
    loadProjectLocalizationFilters: APIScenarioFactory(
      (projectID?: CaFbProject['crhub_project_id']) =>
        get<Record<string, { label: string; value: string | number }[]>>({
          url: `/api/crhub/bl/projects/localization/filters`,
          query: {
            project_id: projectID ? projectID.toString() : undefined,
          },
        }),
    ),
    loadMRGSProjects: APIScenarioFactory(() =>
      get<loadCAProjectsReturn>({ url: `/api/crhub/bl/mrgs-projects/` }),
    ),
    loadProjectsAutotests: APIScenarioFactory(() =>
      get<Record<string, boolean>>({ url: '/api/crhub/bl/projects/autotesting' }),
    ),
    loadDictionaries: APIScenarioFactory((typeId) => {
      return get<{ mask: Dictionary[] }>({ url: _urls.crhub.bl.creatives.types(typeId) });
    }),
    loadCreativeTypes: APIScenarioFactory(() =>
      get<CreativeType[]>({ url: _urls.crhub.bl.creatives.types() }),
    ),
    blCreateCreativeFile: APIScenarioFactory((node_id, creative_id, snapshot) => {
      const payload = {
        node_id,
        creative_id,
        snapshot,
      };
      return post({ url: _urls.crhub.bl.creatives.files(), payload });
    }),
    blUpdateCreativeFile: APIScenarioFactory((node_id, snapshot, creative_id) => {
      const payload = {
        snapshot,
        creative_id,
      };
      return put({ url: _urls.crhub.bl.creatives.files(node_id), payload });
    }),
    loadBLProjects: APIScenarioFactory(() => get<Project[]>({ url: `/api/crhub/bl/projects` })),
    loadBLGenres: APIScenarioFactory((project_id: number) =>
      get<Genre[]>({ url: `/api/crhub/bl/projects/${project_id}/genres` }),
    ),
    loadBLIntros: APIScenarioFactory((project_id: number) =>
      get<Intro[]>({ url: `/api/crhub/bl/projects/${project_id}/intros` }),
    ),
    getBlTeams: APIScenarioFactory(function () {
      return get<{ id: number; name: string }[]>({ url: '/api/crhub/bl/creatives/teams' });
    }),
    loadTags: APIScenarioFactory(() => {
      return get<string[]>({ url: `/api/crhub/bl/creatives/tags` });
    }),
    getSnapshot: APIScenarioFactory((node_id) => {
      return get({ url: _urls.crhub.bl.creatives.files(node_id) });
    }),
    loadCreativeData: APIScenarioFactory((node_id: number) => {
      return get<CreativeFolderServer[]>({
        url: _urls.crhub.bl.creatives.id(),
        query: { node_id },
      });
    }),
    loadCreativeDataHub: APIScenarioFactory((node_id: number[]) => {
      return post<CreativeFolderServer[]>({
        url: `/api/crhub/bl/creatives/list_creatives_hub`,
        payload: { node_id },
      });
    }),
    loadCreativeDataLibrary: APIScenarioFactory((node_id: number[]) => {
      return post<CreativeFolderServer[]>({
        url: `/api/crhub/bl/creatives/list_creatives_library`,
        payload: { node_id },
      });
    }),
    getSocial: APIScenarioFactory(
      (project_id: number, creatives_ids: number[], signal?: AbortSignal) => {
        return post<PlacementData[]>({
          url: `/api/crhub/bl/projects/${project_id}/social`,
          signal,
          payload: { creatives_ids },
        });
      },
    ),
    exportProject: APIScenarioFactory((projectID: string, query: Record<string, string>) =>
      get<{ task_id: string }>({
        url: `/api/crhub/bl/projects/${projectID}/export`,
        query,
      }),
    ),
    loadMisleadLevels: APIScenarioFactory(() =>
      get<MisleadLevel[]>({ url: `/api/crhub/bl/mislead_levels` }),
    ),
    loadConceptTypes: APIScenarioFactory(() =>
      get<ConceptType[]>({ url: `/api/crhub/bl/concept_types` }),
    ),
  },
  tips: {
    tipsGetIsRead: APIScenarioFactory((projectId: string) =>
      get({
        url: `/api/crhub/tips/creatives/is_read/`,
        query: { crhub_project_id: projectId },
      }),
    ),

    tipsSetIsRead: APIScenarioFactory((projectId: number) =>
      post({
        url: `/api/crhub/tips/creatives/read/`,
        payload: { crhub_project_id: projectId },
      }),
    ),

    tipsLoadNewLocalizations: APIScenarioFactory((projectId: number) =>
      get({
        url: `/api/crhub/tips/creatives/localization/`,
        query: { crhub_project_id: projectId.toString(10), page_size: '20' },
      }),
    ),

    tipsLoadBestTitles: APIScenarioFactory(() =>
      get({ url: `/api/crhub/tips/creatives/creative_titles/`, query: { page_size: '20' } }),
    ),

    tipsLoadCountries: APIScenarioFactory((projectId: number, desc: boolean) =>
      get({
        url: `/api/crhub/tips/creatives/countries/`,
        query: {
          crhub_project_id: projectId.toString(10),
          desc: desc ? 'true' : 'false',
          page_size: '20',
        },
      }),
    ),
  },
  creative: {
    creativeAttach: APIScenarioFactory(
      (
        nodeID: number,
        options: BLOptions,
        files: { node_id: number; snapshot: Record<string, string> }[],
      ) => {
        const url = `/api/crhub/bl/creatives/attach`;
        const payload = {
          node_id: nodeID,
          project_id: options.projectId,
          type_id: options.creativeTypeId,
          customer_id: options.managerId,
          executor_id: options.developerId,
          tags: options.tagsActive,
          files,
        };
        return post({ url, payload });
      },
    ),
    patchCreativeScenario: APIScenarioFactory(
      (creativeID: string, payload: CreativePatchOptions) => {
        const {
          tagsActive,
          developerId,
          managerId,
          mislead_level,
          teamID,
          source_creatives,
          plot_recognition,
          ...other
        } = payload;
        const newPayload = pickBy(
          {
            // TODO: перевести весь код в проекте на c managerId/developerId на
            //  customer_id/executor_id
            ...other,
            source_creatives: source_creatives ? source_creatives.map((x) => x.id) : undefined,
            tags: tagsActive,
            customer_id: managerId,
            executor_id: developerId,
            mislead_level,
            team: teamID,
            // Пустой diff не отправляем — иначе бэкенд зря пометит поля как правленные вручную.
            plot_recognition:
              plot_recognition && Object.keys(plot_recognition).length > 0
                ? plot_recognition
                : undefined,
          },
          nonUndefined,
        );
        return patch({ url: _urls.crhub.bl.creatives.id(creativeID), payload: newPayload });
      },
    ),
    loadCreativeForNodeScenario: APIScenarioFactory((node_id: number) => {
      return get<CreativeFolderServer[]>({
        url: _urls.crhub.bl.creatives.id(),
        query: { node_id },
      });
    }),
    createCreaiveFolder: APIScenarioFactory((parentNodeId: number, options: BLOptions) => {
      return post({
        url: _urls.crhub.bl.creatives.id(),
        payload: {
          parent_node_id: parentNodeId,
          project_id: options.projectId,
          node_name: options.creativeName,
          type_id: options.creativeTypeId,
          customer_id: options.managerId,
          executor_id: options.developerId,
          tags: options.tagsActive,
        },
      });
    }),
  },
  publisher: {
    applovin: {
      campaigns: APIScenarioFactory((status?: string) => {
        return get<ApplovinCampaign[]>({
          url: '/api/crhub/applovin/campaigns',
          query: { status: status ?? undefined },
        });
      }),
      apps: APIScenarioFactory(() => {
        return get<ApplovinApp[]>({ url: '/api/crhub/applovin/apps' });
      }),
      nodesInfo: APIScenarioFactory((creative: number, languages: string[], formats: string[]) => {
        return post<NodeMin>({
          url: '/api/crhub/applovin/creative_set/nodes_info/',
          payload: { creative, languages, formats },
        });
      }),
      creative_set: APIScenarioFactory((payload: ApplovinCreativePayloadSet[]) => {
        return post({
          url: '/api/crhub/applovin/creative_set/',
          payload,
        });
      }),
      auto_creative_set: APIScenarioFactory((payload: ApplovinAutoCreativePayloadSet) => {
        return post({
          url: '/api/crhub/applovin/creative_set/combine/',
          payload,
        });
      }),
      combined_creative_set: APIScenarioFactory((payload: ApplovinCombinedCreativePayloadSet) => {
        return post({
          url: '/api/crhub/applovin/creative_set/combined/',
          payload,
        });
      }),
    },
    youtube: {
      loadAccounts: APIScenarioFactory(() => get({ url: '/api/crhub/youtube/accounts/' })),
      loadVideos: APIScenarioFactory((ids: number[]) =>
        get({
          url: '/api/crhub/youtube/videos/',
          query: { ids: ids.map((x) => x.toString(10)).join(',') },
        }),
      ),
      postVideosBulk: APIScenarioFactory((payload: { account: number; node: number }[]) =>
        post({ url: '/api/crhub/youtube/videos/bulk_create/', payload }),
      ),
      getVideosByID: APIScenarioFactory((video_id: number) =>
        get({ url: `/api/crhub/youtube/videos/${video_id}/` }),
      ),
      getQuotaReached: APIScenarioFactory((account: number) =>
        get({ url: `/api/crhub/youtube/videos/quota/${account}/` }),
      ),
    },
    tiktok: {
      getAccounts: APIScenarioFactory((business_account: number) =>
        get<TikTokAccount[]>({
          url: `/api/crhub/tiktok/accounts/`,
          query: { business_account: business_account.toString(10) },
        }),
      ),
      getBusinessAccounts: APIScenarioFactory(() =>
        get<TikTokBusinessAccount[]>({ url: `/api/crhub/tiktok/business_accounts/` }),
      ),
      getLoginURL: APIScenarioFactory((business_account: number) =>
        get<OauthURL>({
          url: `/api/crhub/tiktok/login_url/`,
          query: { business_account: business_account.toString(10) },
        }),
      ),
      getVideos: APIScenarioFactory<TikTokVideo[], any>((ids: number[]) =>
        get<TikTokVideo[]>({
          url: `/api/crhub/tiktok/uploads/`,
          query: { ids: ids.join(',') },
        }),
      ),
      postVideos: APIScenarioFactory(
        (account_id: string, business_account: number, node: number, localization: string) =>
          post<TikTokVideo[]>({
            url: `/api/crhub/tiktok/uploads/`,
            payload: { account_id, business_account, node, localization },
          }),
      ),
      getVideosByID: APIScenarioFactory((id: string) =>
        get<TikTokVideo>({
          url: `/api/crhub/tiktok/uploads/${id}`,
        }),
      ),
      getNodesInfo: APIScenarioFactory<NodeMin[], any>(
        (
          account_id: number,
          business_account: number,
          node: number,
          localization: string,
          formats: string[],
        ) =>
          post<NodeMin[]>({
            url: `/api/crhub/tiktok/uploads/nodes_info/`,
            payload: { account_id, business_account, node, localization, formats },
          }),
      ),
      getWebhookLogin: APIScenarioFactory((auth_code: string, state: string) =>
        get<SuccessResponse>({
          url: `/api/crhub/tiktok/webhooks/login_admin/`,
          query: { auth_code, state },
        }),
      ),
    },
    google: {
      getAccounts: APIScenarioFactory(() =>
        get<GoogleAdsAccount[]>({
          url: `/api/crhub/google/accounts/`,
        }),
      ),
      postAdGroups: APIScenarioFactory(
        (account_id: string, uploadCreativesRequest: GoogleUploadCreativesRequest) =>
          post<GoogleAdGroup[]>({
            url: `/api/crhub/google/accounts/${account_id}/ad_groups/`,
            payload: uploadCreativesRequest,
          }),
      ),
      getAdGroupByID: APIScenarioFactory((account_id: string, ad_group_id: number) =>
        get<GoogleAdGroup>({
          url: `/api/crhub/google/accounts/${account_id}/ad_groups/${ad_group_id}/`,
        }),
      ),
      postNodesInfo: APIScenarioFactory((nodes: Record<number, Record<string, string[]>>) =>
        post<NodeMin[]>({
          url: `/api/crhub/google/nodes_info/`,
          payload: { nodes },
        }),
      ),
      getCampaigns: APIScenarioFactory((account_id: string, signal?: AbortSignal) =>
        get<GoogleCampaign[]>({
          url: `/api/crhub/google/accounts/${account_id}/campaigns/`,
          signal,
        }),
      ),
      getTextTemplates: APIScenarioFactory((account_id: string, campaign_id: string) =>
        get<GoogleTextTemplate[]>({
          url: `/api/crhub/google/accounts/${account_id}/text_templates/`,
          query: { campaign_id },
        }),
      ),
      getLogin: APIScenarioFactory((code: string, state: string) =>
        get<GoogleLoginWebhook>({
          url: `/api/crhub/google/login/`,
          query: { code, state },
        }),
      ),
      getLoginURL: APIScenarioFactory(() =>
        get<void>({
          url: `/api/crhub/google/login_url/`,
        }),
      ),
    },
    unity: {
      loadAccounts: APIScenarioFactory(() => get({ url: '/api/crhub/unity/accounts/' })),
      loadApps: APIScenarioFactory((account_id: number, signal?: AbortSignal) =>
        get<UnityApp[]>({ url: `/api/crhub/unity/accounts/${account_id}/apps/`, signal }),
      ),
      createPack: APIScenarioFactory((payload: CreatePackPayload) =>
        post({ url: `/api/crhub/unity/creative_packs/`, payload }),
      ),
      createPackBulk: APIScenarioFactory((payload: CreatePackPayload[]) =>
        post({ url: `/api/crhub/unity/creative_packs/bulk_create/`, payload }),
      ),
      getCreativePack: APIScenarioFactory((pack_id: number) =>
        get({ url: `/api/crhub/unity/creative_packs/${pack_id}/` }),
      ),
      getNodeInfo: (payload: MiniPayload) =>
        safeShortcuts.post({ url: `/api/crhub/unity/creative_packs/nodes_info/`, payload }),
    },
    iponweb: {
      loadAdvertisers: APIScenarioFactory(() => get({ url: '/api/crhub/iponweb/advertiser/' })),
      publish: APIScenarioFactory(
        (
          advertiser_id: number,
          selected_files: { video_node_id: number; img_node_id: number; is_retargeting: boolean }[],
        ) =>
          post({ url: '/api/crhub/iponweb/upload/', payload: { advertiser_id, selected_files } }),
      ),
    },
    facebook: {
      uploaderUpload: APIScenarioFactory(
        (
          nodeID: number,
          fb_acc_ids: string[],
          access_token_id: number,
          adsetIds?: string[] | null,
        ) =>
          post<AdAccount[]>({
            url: `/api/crhub/uploader/upload/`,
            payload: {
              fb_acc_ids,
              node_id: nodeID,
              access_token_id,
              ...(adsetIds && { adset_ids: adsetIds }),
            },
          }),
      ),
      loadAccounts: APIScenarioFactory((access_token: number, signal?: AbortSignal) =>
        get<AdAccount[]>({
          url: '/api/crhub/facebook/accounts/',
          query: { access_token: access_token.toString(10) },
          signal,
        }),
      ),
      loadManagingAccounts: APIScenarioFactory(() =>
        get<ManagingAccount[]>({
          url: '/api/crhub/facebook/access_tokens/',
        }),
      ),
      loadCampaigns: APIScenarioFactory(
        (access_token: number, account_id: string, signal?: AbortSignal) =>
          get({
            url: '/api/crhub/facebook/campaigns/',
            query: { access_token: access_token.toString(10), account_id },
            signal,
          }),
      ),
      campaignUpload: APIScenarioFactory(
        (
          nodeIDs: number[],
          account_id: string,
          access_token_id: number,
          adsets: { adset_id: string; market: string }[],
          ad_name: string,
          headlines: string[],
          descriptions: string[],
          call_to_action: string,
          adOptions: FacebookAdOptions,
        ) =>
          post<{ adset_id: string; task_id: string }[]>({
            url: `/api/crhub/facebook/upload/`,
            payload: {
              nodes: nodeIDs,
              account_id,
              access_token: access_token_id,
              adsets,
              ad_name,
              headlines,
              descriptions,
              call_to_action,
              ...adOptions,
            },
          }),
      ),
    },
  },
  preview: {
    upload: (node_id: number, file: File) => {
      const payload = new FormData();
      payload.append('file', file);
      return safeShortcuts.post({ url: `/api/crhub/preview/upload-preview/${node_id}`, payload });
    },
    remove: (node_id: number) =>
      safeShortcuts.delete({ url: `/api/crhub/preview/upload-preview/${node_id}` }),
  },
  _unsorted: {
    loadUsers: APIScenarioFactory(() => {
      return get<{ user: CurrentUser }[]>({ url: `/api/crhub/mg/users` });
    }),
    mapFoldersScenario: APIScenarioFactory((parent_node_id: number, names: string[]) => {
      const payload = { parent_node_id, names };
      return post<Record<string, number>>({ url: `/api/crhub/fs/map_folders`, payload });
    }),
    loadCreativeConversionStatusBulk: APIScenarioFactory((ids: number[]) => {
      return post<Record<number, ConversionState>>({
        url: `/api/crhub/bl/creatives/check-many`,
        payload: { ids },
      });
    }),
  },
};
